import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { z } from "zod";
import { passwordResetApiService } from "../../infrastructure/api/password-reset-api-service";
import { Input } from "../components/UI/Input";
import { Button } from "../components/UI/Button";

// 비밀번호 재설정 폼 스키마
const ResetPasswordFormSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "비밀번호는 8자 이상이어야 합니다")
      .max(16, "비밀번호는 16자 이하여야 합니다")
      .regex(/[a-zA-Z]/, "영문자를 포함해야 합니다")
      .regex(/[0-9]/, "숫자를 포함해야 합니다")
      .regex(
        /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/,
        "특수문자를 포함해야 합니다"
      ),
    confirmPassword: z.string().min(1, "비밀번호를 다시 입력해 주세요"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "비밀번호가 일치하지 않습니다",
    path: ["confirmPassword"],
  });

type ResetPasswordForm = z.infer<typeof ResetPasswordFormSchema>;

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(ResetPasswordFormSchema),
  });

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        setTokenError("유효하지 않은 링크입니다.");
        return;
      }

      try {
        const response = await passwordResetApiService.verifyToken(token);
        if (response.data?.valid) {
          setIsValidToken(true);
          setMaskedEmail(response.data.maskedEmail || null);
        } else {
          const reason = response.data?.reason;
          switch (reason) {
            case "EXPIRED":
              setTokenError("링크가 만료되었습니다. 다시 요청해 주세요.");
              break;
            case "ALREADY_USED":
              setTokenError("이미 사용된 링크입니다.");
              break;
            case "INVALID_TOKEN":
            default:
              setTokenError("유효하지 않은 링크입니다.");
              break;
          }
        }
      } catch (err) {
        setTokenError("토큰 검증 중 오류가 발생했습니다.");
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const onSubmit = async (data: ResetPasswordForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await passwordResetApiService.resetPassword({
        token,
        newPassword: data.newPassword,
      });

      if (response.data) {
        setIsSuccess(true);
      } else {
        setError("비밀번호 재설정에 실패했습니다.");
      }
    } catch (err: any) {
      setError(err.message || "비밀번호 재설정 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 로딩 중
  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4285F4] mx-auto mb-4"></div>
          <p className="text-[#555]">링크를 확인하고 있습니다...</p>
        </div>
      </div>
    );
  }

  // 토큰 오류
  if (!isValidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#FEF2F2] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-[#F24A4A]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-[#222] mb-3">
              링크 오류
            </h2>
            <p className="text-[#555] mb-6">{tokenError}</p>
            <Link to="/auth/forgot-password">
              <Button
                type="button"
                className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white"
                style={{ height: "48px", borderRadius: "8px" }}
              >
                비밀번호 찾기 다시 하기
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // 재설정 성공
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#E8EFFF] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-8 h-8 text-[#4285F4]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-[#222] mb-3">
              비밀번호가 변경되었습니다
            </h2>
            <p className="text-[#555] mb-6">
              새로운 비밀번호로 로그인해 주세요.
            </p>
            <Link to="/">
              <Button
                type="button"
                className="w-full bg-[#222] text-white hover:bg-[#333]"
                style={{ height: "48px", borderRadius: "8px" }}
              >
                로그인하러 가기
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // 비밀번호 재설정 폼
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-[#222] mb-2">
            새 비밀번호 설정
          </h1>
          {maskedEmail && (
            <p className="text-[#555]">
              <span className="font-medium">{maskedEmail}</span> 계정의 새
              비밀번호를 설정합니다.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div className="bg-[#EBF3FF] border border-[#4285F4]/20 rounded-[8px] p-3 mb-4">
              <p className="text-sm text-[#4285F4]">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#555] mb-1">
                새 비밀번호
              </label>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="새 비밀번호를 입력해 주세요"
                {...register("newPassword")}
                error={errors.newPassword?.message}
                rightIcon={
                  <button
                    type="button"
                    className="text-xs text-[#888] hover:text-[#555]"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "숨기기" : "보기"}
                  </button>
                }
                style={{
                  width: "100%",
                  height: "48px",
                  borderRadius: "8px",
                }}
              />
              <p className="text-xs text-[#888] mt-1">
                8-16자, 영문+숫자+특수문자 포함
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#555] mb-1">
                비밀번호 확인
              </label>
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="비밀번호를 다시 입력해 주세요"
                {...register("confirmPassword")}
                error={errors.confirmPassword?.message}
                rightIcon={
                  <button
                    type="button"
                    className="text-xs text-[#888] hover:text-[#555]"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? "숨기기" : "보기"}
                  </button>
                }
                style={{
                  width: "100%",
                  height: "48px",
                  borderRadius: "8px",
                }}
              />
            </div>
          </div>

          <div className="mt-6">
            <Button
              type="submit"
              loading={isLoading}
              className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white font-semibold"
              style={{ height: "48px", borderRadius: "8px" }}
            >
              비밀번호 변경하기
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
